import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { createBlock, type CanvasBlock } from '@/lib/cms/card-registry'
import { Blocks, Grid3X3, List, Plus, Search } from 'lucide-react'

/* ── Extracted sub-modules ── */
import type { ForgeTemplate } from './paragon-library/forge-types'
import {
  CATEGORY_COLORS,
  COLLECTIONS,
  DEMO_TEMPLATES,
  FORGE_CATEGORY_TO_BLOCK,
  TIER_COLORS,
} from './paragon-library/forge-types'
import { getPreview } from './paragon-library/forge-previews'
import { ComponentDemoModal, NewComponentModal } from './paragon-library/forge-modals'

/**
 * PARAGON Component Forge — Library Panel
 *
 * Masonry grid of draggable component templates.
 * Templates are fetched from `paragon_forge_templates`.
 *
 * Sub-modules:
 *   forge-types.ts     — shared types & constants
 *   forge-previews.tsx — card thumbnail previews
 *   forge-demos.tsx    — interactive demo components
 *   forge-modals.tsx   — demo modal & new-component modal
 */

/* ═══════════════════════════════════════════════════════════
   LAYOUT SUB-COMPONENTS
   ═══════════════════════════════════════════════════════════ */

function ForgeHeader({
  templatesCount,
  search,
  viewMode,
  onSearch,
  onViewMode,
  onNewComponent,
}: {
  templatesCount: number
  search: string
  viewMode: 'grid' | 'list'
  onSearch: (value: string) => void
  onViewMode: (mode: 'grid' | 'list') => void
  onNewComponent: () => void
}) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06] dark:border-white/[0.04] flex-shrink-0 bg-zinc-50 dark:bg-transparent">
      <div className="flex items-center gap-3">
        <span className="font-['Cormorant_Garamond',_serif] text-[22px] text-zinc-900 dark:text-zinc-50 font-light">The Forge</span>
        <span className="text-[11px] text-zinc-400 dark:text-white/20 tracking-wider font-mono">Component Library</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/[0.05] dark:bg-white/[0.04] border border-black/[0.07] dark:border-white/[0.06] text-zinc-400 dark:text-white/25 font-mono">{templatesCount}</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/[0.04] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.06]">
          <Search className="w-3.5 h-3.5 text-zinc-400 dark:text-white/20" />
          <input
            type="text"
            value={search}
            onChange={e => onSearch(e.target.value)}
            placeholder="Search components…"
            aria-label="Search components"
            className="bg-transparent text-[12px] text-zinc-900 dark:text-zinc-50 placeholder-zinc-400/60 dark:placeholder-white/15 outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.3)] w-36"
          />
        </div>
        <div className="flex items-center bg-black/[0.04] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.06] rounded-lg p-0.5">
          <button
            onClick={() => onViewMode('grid')}
            aria-label="Grid view"
            aria-pressed={viewMode === 'grid'}
            className={`p-1.5 rounded-md transition-[background-color,color,border-color,opacity] ${viewMode === 'grid' ? 'bg-white dark:bg-white/[0.07] text-zinc-700 dark:text-white/70 shadow-sm' : 'text-zinc-400 dark:text-white/25 hover:text-zinc-600 dark:hover:text-white/40'}`}
          >
            <Grid3X3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onViewMode('list')}
            aria-label="List view"
            aria-pressed={viewMode === 'list'}
            className={`p-1.5 rounded-md transition-[background-color,color,border-color,opacity] ${viewMode === 'list' ? 'bg-white dark:bg-white/[0.07] text-zinc-700 dark:text-white/70 shadow-sm' : 'text-zinc-400 dark:text-white/25 hover:text-zinc-600 dark:hover:text-white/40'}`}
          >
            <List className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          onClick={() => onNewComponent()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-black font-medium bg-[#C9A84C] hover:bg-[#D4B85C] transition-[background-color,color,border-color,opacity]">
          <Plus className="w-3.5 h-3.5" />
          New Component
        </button>
      </div>
    </div>
  )
}

function ForgeCategorySidebar({
  categories,
  activeCategory,
  categoryCounts,
  onSelect,
}: {
  categories: string[]
  activeCategory: string
  categoryCounts: Record<string, number>
  onSelect: (category: string) => void
}) {
  return (
    <div className="w-[180px] flex-shrink-0 border-r border-black/[0.05] dark:border-white/[0.04] p-4 overflow-y-auto bg-zinc-50/50 dark:bg-transparent">
      <div className="text-[10px] text-zinc-400 dark:text-white/20 tracking-wider mb-3 font-mono">CATEGORIES</div>
      <div className="space-y-0.5 mb-6">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => onSelect(cat)}
            aria-pressed={activeCategory === cat}
            className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[12px] transition-[background-color,color,border-color,opacity] ${
              activeCategory === cat
                ? 'bg-black/[0.06] dark:bg-white/[0.05] text-zinc-900 dark:text-zinc-50'
                : 'text-zinc-500 dark:text-white/30 hover:bg-black/[0.04] dark:hover:bg-white/[0.02] hover:text-zinc-700 dark:hover:text-white/50'
            }`}
          >
            <span>{cat.charAt(0).toUpperCase() + cat.slice(1)}</span>
            <span className="text-[10px] font-mono text-zinc-400 dark:text-white/15">{categoryCounts[cat] || 0}</span>
          </button>
        ))}
      </div>

      <div className="text-[10px] text-zinc-400 dark:text-white/20 tracking-wider mb-3 font-mono">COLLECTIONS</div>
      <div className="space-y-0.5">
        {COLLECTIONS.map((col) => (
          <div key={col} className="px-2 py-1.5 text-[12px] text-zinc-400 dark:text-white/20 rounded-lg">
            {col}
          </div>
        ))}
      </div>
    </div>
  )
}

function ForgeDetailPanel({
  selectedComponent,
  tierOf,
  onCopy,
}: {
  selectedComponent: ForgeTemplate
  tierOf: (template: ForgeTemplate) => string
  onCopy: (template: ForgeTemplate) => void
}) {
  const tier = tierOf(selectedComponent)
  const tierCls = TIER_COLORS[tier] ?? TIER_COLORS.All

  return (
    <div className="w-[280px] flex-shrink-0 border-l border-black/[0.05] dark:border-white/[0.04] p-4 overflow-y-auto bg-zinc-50/50 dark:bg-transparent">
      <div className="mb-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className="text-[14px] text-zinc-900 dark:text-zinc-50 font-medium leading-tight">{selectedComponent.name}</span>
          <span className="text-[10px] font-mono text-zinc-400 dark:text-white/20 flex-shrink-0">v1.0</span>
        </div>
        <span className={`text-[9px] px-1.5 py-0.5 rounded border font-mono ${tierCls}`}>{tier}</span>
        <p className="text-[11px] text-zinc-500 dark:text-white/30 leading-relaxed mt-2">{selectedComponent.category.charAt(0).toUpperCase() + selectedComponent.category.slice(1)} component ready for tenant deployment.</p>
      </div>

      <div className="h-[120px] rounded-xl bg-black/[0.03] dark:bg-white/[0.02] border border-black/[0.06] dark:border-white/[0.06] overflow-hidden mb-4">
        {selectedComponent.thumbnail_url
          ? <img src={selectedComponent.thumbnail_url} alt={selectedComponent.name} className="w-full h-full object-cover" />
          : getPreview(selectedComponent)
        }
      </div>

      <div className="mb-4">
        <div className="text-[10px] text-zinc-400 dark:text-white/20 tracking-wider mb-2 font-mono">TAGS</div>
        <div className="flex flex-wrap gap-1.5">
          {[selectedComponent.category, 'component', 'forge'].map((tag) => {
            const colors = CATEGORY_COLORS[tag] || { bg: 'rgba(113,113,122,0.15)', text: '#71717a' }
            return <span key={tag} className="text-[10px] px-2 py-0.5 rounded font-mono" style={{ background: colors.bg, color: colors.text }}>{tag}</span>
          })}
        </div>
      </div>

      <div className="mb-4">
        <div className="text-[10px] text-zinc-400 dark:text-white/20 tracking-wider mb-2 font-mono">USAGE</div>
        <div className="text-[12px] text-zinc-500 dark:text-white/35">Used by — tenants</div>
        <div className="text-[11px] text-zinc-400 dark:text-white/20 font-mono mt-0.5">{selectedComponent.category.charAt(0).toUpperCase() + selectedComponent.category.slice(1)} Collection</div>
      </div>

      <div className="space-y-2">
        <button onClick={() => onCopy(selectedComponent)} className="w-full py-2.5 rounded-lg text-[12px] font-medium text-black bg-[#C9A84C] hover:bg-[#D4B85C] transition-[background-color,color,border-color,opacity]">Copy to Clipboard</button>
        <button className="w-full py-2.5 rounded-lg text-[12px] text-zinc-500 dark:text-white/35 border border-black/[0.07] dark:border-white/[0.06] hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-colors">View Source</button>
      </div>
    </div>
  )
}

function ForgeTemplatesPanel({
  filtered,
  viewMode,
  selectedComponent,
  insertTarget,
  tenants,
  tierOf,
  onDragStart,
  onCardClick,
  onToggleInsert,
  onInsert,
}: {
  filtered: ForgeTemplate[]
  viewMode: 'grid' | 'list'
  selectedComponent: ForgeTemplate | null
  insertTarget: string | null
  tenants: { id: string; name: string; slug: string }[]
  tierOf: (template: ForgeTemplate) => string
  onDragStart: (e: React.DragEvent, template: ForgeTemplate) => void
  onCardClick: (template: ForgeTemplate) => void
  onToggleInsert: (templateId: string) => void
  onInsert: (template: ForgeTemplate, tenantSlug: string) => void
}) {
  if (filtered.length === 0) return <div className="text-center text-zinc-400 dark:text-white/15 py-12 text-[13px]">No components match filter</div>

  if (viewMode === 'list') {
    return (
      <div className="space-y-1">
        {filtered.map((template) => {
          const tier = tierOf(template)
          const tierCls = TIER_COLORS[tier] ?? TIER_COLORS.All
          const colors = CATEGORY_COLORS[template.category.toLowerCase()] || { bg: 'rgba(113,113,122,0.15)', text: '#71717a' }

          return (
            <div key={template.id} draggable onDragStart={(e) => onDragStart(e, template)} onClick={() => onCardClick(template)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCardClick(template) } }} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-black/[0.07] dark:border-white/[0.05] bg-black/[0.02] dark:bg-white/[0.02] hover:bg-black/[0.04] dark:hover:bg-white/[0.03] hover:border-black/[0.12] dark:hover:border-white/[0.09] transition-[background-color,color,border-color,opacity] cursor-pointer">
              <div className="w-10 h-10 rounded-lg bg-black/[0.04] dark:bg-white/[0.03] border border-black/[0.06] dark:border-white/[0.05] flex items-center justify-center flex-shrink-0"><Blocks className="w-4 h-4 text-zinc-400 dark:text-white/20" /></div>
              <div className="flex-1 min-w-0"><div className="flex items-center gap-2"><span className="text-[13px] text-zinc-900 dark:text-zinc-50">{template.name}</span><span className="text-[10px] font-mono text-zinc-400 dark:text-white/20">v1.0</span><span className={`text-[9px] px-1.5 py-0.5 rounded border font-mono ${tierCls}`}>{tier}</span></div><div className="text-[11px] text-zinc-400 dark:text-white/25 truncate mt-0.5">{template.category.charAt(0).toUpperCase() + template.category.slice(1)} component</div></div>
              <span className="text-[10px] px-2 py-0.5 rounded font-mono" style={{ background: colors.bg, color: colors.text }}>{template.category}</span>
              <button onClick={(e) => { e.stopPropagation(); onToggleInsert(template.id) }} aria-expanded={insertTarget === template.id} aria-controls={`forge-insert-menu-${template.id}`} className="flex-shrink-0 text-[11px] px-2.5 py-1 rounded-lg border border-black/[0.07] dark:border-white/[0.06] text-zinc-500 dark:text-white/30 hover:text-zinc-700 dark:hover:text-white/50 hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-colors relative">Copy
                {insertTarget === template.id && (
                  <div id={`forge-insert-menu-${template.id}`} className="absolute bottom-full right-0 mb-2 bg-white dark:bg-[#0f0f14] border border-black/[0.09] dark:border-white/[0.08] rounded-xl p-2 min-w-[180px] z-50 shadow-xl">
                    <div className="text-[10px] text-zinc-400 dark:text-white/20 tracking-wider px-2 py-1 font-mono">SELECT TENANT</div>
                    {tenants.length === 0 ? <div className="px-2 py-1 text-[11px] text-zinc-400 dark:text-white/20">No tenants</div> : tenants.map((t) => (
                      <button key={t.id} onClick={(e) => { e.stopPropagation(); onInsert(template, t.slug) }} className="block w-full text-left px-2 py-1.5 rounded-lg text-[12px] text-zinc-900 dark:text-zinc-50 hover:bg-black/[0.05] dark:hover:bg-white/[0.05] transition-colors">{t.name}<span className="block text-[10px] text-zinc-400 dark:text-white/25">/{t.slug}</span></button>
                    ))}
                  </div>
                )}
              </button>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-3 gap-3">
      {filtered.map((template) => {
        const isSelected = selectedComponent?.id === template.id
        const tier = tierOf(template)
        const tierCls = TIER_COLORS[tier] ?? TIER_COLORS.All

        return (
          <div key={template.id} draggable onDragStart={(e) => onDragStart(e, template)} onClick={() => onCardClick(template)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCardClick(template) } }} className={`rounded-xl border cursor-pointer transition-[background-color,color,border-color,opacity] duration-150 overflow-hidden ${isSelected ? 'border-[rgba(201,168,76,0.25)] bg-[rgba(201,168,76,0.03)]' : 'border-black/[0.08] dark:border-white/[0.06] bg-black/[0.02] dark:bg-white/[0.02] hover:border-black/[0.14] dark:hover:border-white/[0.1]'}`}>
            <div className="h-[100px] rounded-t-xl bg-black/[0.03] dark:bg-white/[0.01] border-b border-black/[0.06] dark:border-white/[0.04] overflow-hidden">{template.thumbnail_url ? <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover" /> : getPreview(template)}</div>
            <div className="p-3">
              <div className="flex items-start justify-between gap-1 mb-1"><span className="text-[12px] text-zinc-900 dark:text-zinc-50 font-medium leading-tight">{template.name}</span><span className={`text-[9px] px-1.5 py-0.5 rounded border flex-shrink-0 font-mono ${tierCls}`}>{tier}</span></div>
              <div className="text-[10px] text-zinc-400 dark:text-white/25 line-clamp-2 leading-relaxed">{template.category.charAt(0).toUpperCase() + template.category.slice(1)} component</div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[9px] font-mono text-zinc-400 dark:text-white/15">v1.0</span>
                <div className="relative">
                  <button onClick={(e) => { e.stopPropagation(); onToggleInsert(template.id) }} aria-expanded={insertTarget === template.id} aria-controls={`forge-insert-menu-${template.id}`} className="text-[10px] px-2 py-0.5 rounded text-[rgba(201,168,76,0.5)] hover:text-[rgba(201,168,76,0.8)] transition-colors">Insert →</button>
                  {insertTarget === template.id && (
                    <div id={`forge-insert-menu-${template.id}`} className="absolute bottom-full right-0 mb-2 bg-white dark:bg-[#0f0f14] border border-black/[0.09] dark:border-white/[0.08] rounded-xl p-2 min-w-[180px] z-50 shadow-xl">
                      <div className="text-[10px] text-zinc-400 dark:text-white/20 tracking-wider px-2 py-1 font-mono">SELECT TENANT</div>
                      {tenants.length === 0 ? <div className="px-2 py-1 text-[11px] text-zinc-400 dark:text-white/20">No tenants</div> : tenants.map((t) => (
                        <button key={t.id} onClick={(e) => { e.stopPropagation(); onInsert(template, t.slug) }} className="block w-full text-left px-2 py-1.5 rounded-lg text-[12px] text-zinc-900 dark:text-zinc-50 hover:bg-black/[0.05] dark:hover:bg-white/[0.05] transition-colors">{t.name}<span className="block text-[10px] text-zinc-400 dark:text-white/25">/{t.slug}</span></button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */

export default function ParagonLibraryPanel() {
  const [uiState, setUiState] = useState<{
    activeCategory: string
    insertTarget: string | null
    demoTarget: ForgeTemplate | null
    viewMode: 'grid' | 'list'
    selectedComponent: ForgeTemplate | null
    search: string
    showNewComponent: boolean
  }>({
    activeCategory: 'all',
    insertTarget: null,
    demoTarget: null,
    viewMode: 'grid',
    selectedComponent: null,
    search: '',
    showNewComponent: false,
  })
  const { activeCategory, insertTarget, demoTarget, viewMode, selectedComponent, search, showNewComponent } = uiState
  const navigate = useNavigate()

  const { data: dbTemplates = [] } = useQuery({
    queryKey: ['paragon-forge-templates'],
    queryFn: async () => {
      const { data, error } = await supabase.from('paragon_forge_templates')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []).map(row => ({
        ...row,
        config: (row.ast_payload as Record<string, unknown> | null) ?? {},
      })) as ForgeTemplate[]
    },
    staleTime: 5 * 60_000,
  })

  const { data: tenants = [] } = useQuery({
    queryKey: ['paragon-forge-tenants'],
    queryFn: async () => {
      const { data, error } = await supabase.from('tenants')
        .select('id, name, slug')
        .order('name')
      if (error) throw error
      return data ?? []
    },
    staleTime: 5 * 60_000,
  })

  const templates = dbTemplates.length > 0 ? dbTemplates : DEMO_TEMPLATES

  const categories = useMemo(() => {
    const cats = new Set(templates.map((t) => t.category.toLowerCase()))
    return ['all', ...Array.from(cats)]
  }, [templates])

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: templates.length }
    templates.forEach((t) => { const c = t.category.toLowerCase(); counts[c] = (counts[c] || 0) + 1 })
    return counts
  }, [templates])

  const filtered = useMemo(() => {
    let result = activeCategory === 'all' ? templates : templates.filter(t => t.category.toLowerCase() === activeCategory)
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(t => t.name.toLowerCase().includes(q) || t.category.toLowerCase().includes(q))
    }
    return result
  }, [templates, activeCategory, search])

  const handleDragStart = (e: React.DragEvent, template: ForgeTemplate) => {
    e.dataTransfer.setData('application/json', JSON.stringify(template))
    e.dataTransfer.setData('application/x-forge', JSON.stringify(template))
    const blockType = FORGE_CATEGORY_TO_BLOCK[template.category.toLowerCase()] || 'cta-block'
    e.dataTransfer.setData('application/x-card', JSON.stringify({ type: blockType }))
    e.dataTransfer.effectAllowed = 'copy'
  }

  const handleInsertIntoEditor = useCallback((template: ForgeTemplate, tenantSlug: string) => {
    const blockType = FORGE_CATEGORY_TO_BLOCK[template.category.toLowerCase()] || 'cta-block'
    const block: CanvasBlock = createBlock(blockType, {
      data: { ...((template.config || {}) as Record<string, string>), __forgeTemplateName: template.name },
    })
    setUiState(prev => ({ ...prev, insertTarget: null }))
    navigate(`/${tenantSlug}/admin/editor`, { state: { templateBlocks: [block] } })
  }, [navigate])

  const handleCardClick = (template: ForgeTemplate) => {
    setUiState(prev => ({ ...prev, selectedComponent: template, demoTarget: template }))
  }

  const tierOf = (t: ForgeTemplate): string => {
    if (t.category === 'showcase' || t.category === 'interactive') return 'Pro+'
    return 'All'
  }

  return (
    <>
      {/* Scrollbar + focus-visible styles are provided globally by globals.css */}

      <div className="flex flex-col h-full overflow-hidden">

        <ForgeHeader
          templatesCount={templates.length}
          search={search}
          viewMode={viewMode}
          onSearch={(value) => setUiState(prev => ({ ...prev, search: value }))}
          onViewMode={(mode) => setUiState(prev => ({ ...prev, viewMode: mode }))}
          onNewComponent={() => setUiState(prev => ({ ...prev, showNewComponent: true }))}
        />

        {/* ── 3-panel body ── */}
        <div className="flex flex-1 overflow-hidden">

          <ForgeCategorySidebar
            categories={categories}
            activeCategory={activeCategory}
            categoryCounts={categoryCounts}
            onSelect={(category) => setUiState(prev => ({ ...prev, activeCategory: category }))}
          />

          {/* Main content */}
          <div className="flex-1 overflow-y-auto p-6">
            <ForgeTemplatesPanel
              filtered={filtered}
              viewMode={viewMode}
              selectedComponent={selectedComponent}
              insertTarget={insertTarget}
              tenants={tenants}
              tierOf={tierOf}
              onDragStart={handleDragStart}
              onCardClick={handleCardClick}
              onToggleInsert={(templateId) => setUiState(prev => ({ ...prev, insertTarget: prev.insertTarget === templateId ? null : templateId }))}
              onInsert={handleInsertIntoEditor}
            />
          </div>

          {/* Right detail panel */}
          {selectedComponent && (
            <ForgeDetailPanel
              selectedComponent={selectedComponent}
              tierOf={tierOf}
              onCopy={(template) => setUiState(prev => ({ ...prev, demoTarget: template }))}
            />
          )}

        </div>
      </div>

      {/* Demo modal */}
      {demoTarget && (
        <ComponentDemoModal
          template={demoTarget}
          onClose={() => setUiState(prev => ({ ...prev, demoTarget: null }))}
        />
      )}

      {/* New Component modal */}
      {showNewComponent && (
        <NewComponentModal
          onClose={() => setUiState(prev => ({ ...prev, showNewComponent: false }))}
          onCreated={() => setUiState(prev => ({ ...prev, showNewComponent: false }))}
        />
      )}
    </>
  )
}
